import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, Check, Loader2, Plus, Save, Trash2,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";

function ConsentLetter({ childName }) {
  const name = childName || "……………………………………";
  return (
    <div className="rounded-md border border-border bg-white p-4 space-y-3 max-h-96 overflow-y-auto" data-testid="consent-letter">
      <p className="text-[11px] text-slate-500 leading-relaxed">
        Deluxe Healthcare Services Ltd · Tel: 0208 262 9291 · Mob: 07397 780653 · admin@deluxehealthcare.co.uk ·
        www.deluxehealthcare.co.uk · Office 608, 321-323 High Road, Chadwell Heath, RM6 6AX
      </p>
      <h4 className="font-heading text-sm font-bold tracking-wide text-slate-900">PARENTAL CONSENT FORM</h4>
      <p className="text-sm text-slate-700">Dear Parent/carer,</p>
      <p className="text-sm text-slate-700 leading-relaxed">
        Your child <span className="font-semibold underline decoration-dotted">{name}</span> has been referred to our
        Independent Travel Training program by the London Borough of Barking and Dagenham. Independent Travel Training
        is an essential life skill for your child. Being independently travel trained will increase their confidence
        and opportunities for employment, further education and social activities.
      </p>
      <p className="text-sm text-slate-700 leading-relaxed">
        Travel training involves taking the trainee out on public transport and practising journeys to and from home
        and school. Travel training will take place over <strong>12 WEEKS</strong> from Monday to Friday during term time.
      </p>
      <p className="text-sm text-slate-700 leading-relaxed">
        Your child will be closely supervised by an experienced trainer, and you will be consulted throughout your
        child&rsquo;s travel training journey.
      </p>
      <p className="text-sm text-slate-700 leading-relaxed">
        Students participating in our Independent Travel Training program receive instructions in:
      </p>
      <ul className="list-disc pl-6 text-sm text-slate-700 space-y-0.5">
        <li>Route planning</li>
        <li>Getting about</li>
        <li>Behaving appropriately</li>
        <li>Staying safe</li>
        <li>Problem solving to manage foreseen and unforeseen situations</li>
      </ul>
      <p className="text-sm text-slate-700 leading-relaxed">
        Your child will require a Zip card, a valid student ID card and a mobile phone with credit on it.
      </p>
      <p className="text-sm text-slate-700 leading-relaxed">
        Your child will be assessed for their suitability for independent travel before and during their training.
        If, at the end of their Independent Travel Training, your child is deemed capable of travelling independently,
        this will then become their regular method of travel as a direct replacement for assisted transport.
      </p>
      <p className="text-sm text-slate-700 leading-relaxed">
        During your child&rsquo;s Independent Travel Training, photographs may be taken and used to encourage other
        trainees or for promotional literature.
      </p>
      <p className="text-sm text-slate-700 leading-relaxed">
        Sandra Allen<br />Deputy Manager<br />Deluxe Healthcare Services Ltd
      </p>
      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-sm text-slate-700 leading-relaxed">
          By signing this form, you are giving your permission for your son / daughter to undertake Independent
          Travel Training sessions in order to enable them to travel independently to/from home to school.
        </p>
        <ul className="list-disc pl-6 text-sm text-slate-700 space-y-0.5">
          <li>I agree to my child participating in the Independent Travel Training program.</li>
          <li>I understand that during the training, photographs may be taken and used to encourage and motivate other travel trainees.</li>
          <li>I agree to my child carrying a Travel Training Wallet while travel training, in case of emergencies.</li>
        </ul>
        <p className="text-xs text-slate-500">
          Medical and other relevant information is recorded in the Health &amp; Safety step of this referral.
        </p>
      </div>
    </div>
  );
}

const STEPS = [
  "Referral / Council", "Child Details", "Parent/Carer", "School", "SEND & Support",
  "Health & Safety", "Consent & Authority", "Documents & Photograph", "Trainer Assignment", "Review & Submit",
];

const CONSENT_TYPES = [
  { key: "participation", label: "Participation in Independent Travel Training" },
  { key: "parent_communication", label: "Parent/Carer communication" },
  { key: "information_sharing", label: "Relevant information sharing" },
  { key: "photography", label: "Photography (identification)" },
  { key: "training_evidence_photos", label: "Training evidence photographs" },
  { key: "emergency_contact", label: "Emergency contact" },
  { key: "council_info_sharing", label: "Local Authority information sharing" },
  { key: "school_info_sharing", label: "School information sharing" },
];

const EMPTY_PARENT = {
  name: "", relationship: "", phone: "", email: "", is_primary_contact: false, is_emergency_contact: false,
  authorised_to_receive_info: false, preferred_contact_method: "", notes: "",
};

function Field({ label, children, hint, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default function ChildWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [councils, setCouncils] = useState([]);
  const [schools, setSchools] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [duplicates, setDuplicates] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    // referral / council
    council_id: "", referral_date: "", council_reference: "", po_reference: "",
    referrer_name: "", referrer_organisation: "", referrer_job_title: "", referrer_email: "", referrer_phone: "",
    reason: "", requested_service: "", information_provided: "", priority: "standard",
    // child
    first_name: "", middle_name: "", surname: "", preferred_name: "", date_of_birth: "", gender: "",
    primary_language: "", communication_method: "",
    address_line1: "", address_line2: "", address_town: "", address_postcode: "",
    // school
    school_id: "",
    // SEND
    send_primary_need: "", send_additional_needs: "", communication_needs: "", mobility_requirements: "",
    sensory_considerations: "", behavioural_considerations: "", learning_considerations: "",
    known_triggers: "", strategies_help: "", reasonable_adjustments: "", additional_info_safe_travel: "",
    // health
    health_allergies: "", health_medication: "", health_conditions: "", health_emergency_action: "",
    health_mobility: "", health_other: "",
    // consent: {type: status}
    consents: {},
    consent_provided_by: "",
    signature_data: null,
    // assignment
    trainer_id: "",
    assignment_notes: "",
  });
  const [parents, setParents] = useState([{ ...EMPTY_PARENT }]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    api.get("/councils").then((r) => setCouncils(r.data.items)).catch(() => {});
    api.get("/schools").then((r) => setSchools(r.data.items)).catch(() => {});
    api.get("/staff", { params: { role: "travel_trainer", status: "active" } }).then((r) => setTrainers(r.data.items)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkDuplicates = async () => {
    if (!form.first_name || !form.surname) return true;
    try {
      const { data } = await api.post("/children/check-duplicates", {
        first_name: form.first_name, surname: form.surname,
        date_of_birth: form.date_of_birth || null,
        school_id: form.school_id || null,
        council_reference: form.council_reference || null,
      });
      setDuplicates(data.matches);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    }
  };

  const next = async () => {
    if (step === 1) {
      if (!form.first_name.trim() || !form.surname.trim()) {
        toast.error("First name and surname are required");
        return;
      }
      const ok = await checkDuplicates();
      if (!ok) return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (draft) => {
    if (!form.first_name.trim() || !form.surname.trim()) {
      toast.error("Child first name and surname are required (Step 2)");
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const childPayload = {};
      [
        "first_name", "middle_name", "surname", "preferred_name", "date_of_birth", "gender",
        "primary_language", "communication_method", "address_line1", "address_line2",
        "address_town", "address_postcode", "council_id", "school_id", "council_reference",
        "send_primary_need", "send_additional_needs", "communication_needs", "mobility_requirements",
        "sensory_considerations", "behavioural_considerations", "learning_considerations",
        "known_triggers", "strategies_help", "reasonable_adjustments", "additional_info_safe_travel",
        "health_allergies", "health_medication", "health_conditions", "health_emergency_action",
        "health_mobility", "health_other",
      ].forEach((k) => {
        if (form[k]) childPayload[k] = form[k];
      });
      const { data: child } = await api.post("/children", childPayload);

      const referralPayload = {
        child_id: child.id,
        council_id: form.council_id || null,
        school_id: form.school_id || null,
        referral_date: form.referral_date || null,
        council_reference: form.council_reference || null,
        po_reference: form.po_reference || null,
        referrer_name: form.referrer_name || null,
        referrer_organisation: form.referrer_organisation || null,
        referrer_job_title: form.referrer_job_title || null,
        referrer_email: form.referrer_email || null,
        referrer_phone: form.referrer_phone || null,
        reason: form.reason || null,
        requested_service: form.requested_service || null,
        information_provided: form.information_provided || null,
        priority: form.priority,
        status: "new",
      };
      const { data: referral } = await api.post("/referrals", referralPayload);

      for (const p of parents) {
        if (!p.name.trim()) continue;
        await api.post(`/children/${child.id}/parents`, {
          name: p.name, relationship: p.relationship || null, phone: p.phone || null,
          email: p.email || null, preferred_contact_method: p.preferred_contact_method || null,
          is_primary_contact: p.is_primary_contact, is_emergency_contact: p.is_emergency_contact,
          authorised_to_receive_info: p.authorised_to_receive_info, notes: p.notes || null,
        });
      }

      let signatureDocId = null;
      if (form.signature_data) {
        const blob = await (await fetch(form.signature_data)).blob();
        const fd = new FormData();
        fd.append("file", new File([blob], "onscreen-signature.png", { type: "image/png" }));
        fd.append("category", "consent_document");
        fd.append("child_id", child.id);
        fd.append("referral_id", referral.id);
        fd.append("description", `On-screen signature — ${form.consent_provided_by || "parent/carer"}`);
        const { data: sigDoc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        signatureDocId = sigDoc.id;
      }

      for (const [type, status] of Object.entries(form.consents)) {
        if (!status || status === "none") continue;
        await api.post(`/children/${child.id}/consents`, {
          type, status,
          provided_by_name: form.consent_provided_by || null,
          date_provided: form.referral_date || null,
          document_id: signatureDocId,
          method: signatureDocId ? "onscreen_signature" : null,
        });
      }

      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        fd.append("category", f.category);
        fd.append("child_id", child.id);
        if (f.category === "referral_document") fd.append("referral_id", referral.id);
        if (f.description) fd.append("description", f.description);
        if (f.category === "child_photo") fd.append("consent_status", form.consents.photography && form.consents.photography !== "none" ? form.consents.photography : "pending");
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }

      if (form.trainer_id) {
        await api.post(`/children/${child.id}/assignments`, {
          trainer_id: form.trainer_id, assignment_type: "primary", notes: form.assignment_notes || null,
        });
      }

      toast.success(draft ? `Draft saved — case ${child.case_id}` : `Referral submitted — case ${child.case_id}`);
      navigate(`/children/${child.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const council = councils.find((c) => c.id === form.council_id);
  const school = schools.find((s) => s.id === form.school_id);
  const trainer = trainers.find((t) => t.id === form.trainer_id);

  return (
    <div data-testid="child-wizard" className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">New referral</h1>
        <p className="text-sm text-slate-600 mt-1">Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>
      </div>

      {/* Step indicator */}
      <ol className="flex flex-wrap gap-1.5" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <li key={s}>
            <button
              type="button"
              data-testid={`wizard-step-${i}`}
              onClick={() => setStep(i)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-200 min-h-[32px] ${
                i === step ? "bg-primary text-white" : i < step ? "bg-ukgreen-light text-ukgreen" : "bg-slate-100 text-slate-500"
              }`}
            >
              {i + 1}. {s}
            </button>
          </li>
        ))}
      </ol>

      <Card>
        <CardContent className="p-5 sm:p-6">
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-referral">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Commissioning Local Authority *" testid="f-council">
                  <Select value={form.council_id} onValueChange={(v) => set("council_id", v)}>
                    <SelectTrigger data-testid="wizard-council-select"><SelectValue placeholder="Select local authority" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {councils.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`wizard-council-${c.council_number}`}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Referral date">
                  <Input data-testid="wizard-referral-date" type="date" value={form.referral_date} onChange={(e) => set("referral_date", e.target.value)} />
                </Field>
                <Field label="Council case / referral reference">
                  <Input data-testid="wizard-council-reference" value={form.council_reference} onChange={(e) => set("council_reference", e.target.value)} />
                </Field>
                <Field label="Purchase order / authorisation reference">
                  <Input data-testid="wizard-po-reference" value={form.po_reference} onChange={(e) => set("po_reference", e.target.value)} />
                </Field>
                <Field label="Referrer name">
                  <Input data-testid="wizard-referrer-name" value={form.referrer_name} onChange={(e) => set("referrer_name", e.target.value)} />
                </Field>
                <Field label="Referrer organisation">
                  <Input data-testid="wizard-referrer-org" value={form.referrer_organisation} onChange={(e) => set("referrer_organisation", e.target.value)} />
                </Field>
                <Field label="Referrer job title">
                  <Input data-testid="wizard-referrer-title" value={form.referrer_job_title} onChange={(e) => set("referrer_job_title", e.target.value)} />
                </Field>
                <Field label="Referrer email">
                  <Input data-testid="wizard-referrer-email" type="email" value={form.referrer_email} onChange={(e) => set("referrer_email", e.target.value)} />
                </Field>
                <Field label="Referrer telephone">
                  <Input data-testid="wizard-referrer-phone" value={form.referrer_phone} onChange={(e) => set("referrer_phone", e.target.value)} />
                </Field>
                <Field label="Priority">
                  <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                    <SelectTrigger data-testid="wizard-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Reason for referral">
                <Textarea data-testid="wizard-reason" rows={3} value={form.reason} onChange={(e) => set("reason", e.target.value)} />
              </Field>
              <Field label="Requested service">
                <Input data-testid="wizard-requested-service" value={form.requested_service} onChange={(e) => set("requested_service", e.target.value)} placeholder="e.g. Independent travel training — home to school" />
              </Field>
              <Field label="Relevant information provided">
                <Textarea data-testid="wizard-info-provided" rows={2} value={form.information_provided} onChange={(e) => set("information_provided", e.target.value)} />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-child">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name *"><Input data-testid="wizard-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
                <Field label="Surname *"><Input data-testid="wizard-surname" value={form.surname} onChange={(e) => set("surname", e.target.value)} /></Field>
                <Field label="Middle name"><Input data-testid="wizard-middle-name" value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} /></Field>
                <Field label="Preferred name"><Input data-testid="wizard-preferred-name" value={form.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} /></Field>
                <Field label="Date of birth"><Input data-testid="wizard-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
                <Field label="Gender (only if legitimately required)"><Input data-testid="wizard-gender" value={form.gender} onChange={(e) => set("gender", e.target.value)} /></Field>
                <Field label="Primary language"><Input data-testid="wizard-language" value={form.primary_language} onChange={(e) => set("primary_language", e.target.value)} /></Field>
                <Field label="Preferred communication method"><Input data-testid="wizard-comm-method" value={form.communication_method} onChange={(e) => set("communication_method", e.target.value)} /></Field>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="wizard-address-section">
                <p className="text-xs font-semibold text-amber-900">Home address is sensitive — access is restricted to staff with an operational requirement.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Address line 1"><Input data-testid="wizard-address1" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} /></Field>
                  <Field label="Address line 2"><Input data-testid="wizard-address2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} /></Field>
                  <Field label="Town / City"><Input data-testid="wizard-town" value={form.address_town} onChange={(e) => set("address_town", e.target.value)} /></Field>
                  <Field label="Postcode"><Input data-testid="wizard-postcode" value={form.address_postcode} onChange={(e) => set("address_postcode", e.target.value)} /></Field>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-parents">
              {parents.map((p, i) => (
                <div key={i} className="rounded-md border border-border p-4 space-y-3" data-testid={`wizard-parent-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">Parent / Carer {i + 1}</p>
                    {parents.length > 1 && (
                      <Button type="button" variant="ghost" className="h-9 text-red-600" data-testid={`wizard-parent-remove-${i}`}
                        onClick={() => setParents(parents.filter((_, j) => j !== i))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Full name *"><Input data-testid={`parent-name-${i}`} value={p.name} onChange={(e) => setParents(parents.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></Field>
                    <Field label="Relationship to child"><Input data-testid={`parent-relationship-${i}`} value={p.relationship} onChange={(e) => setParents(parents.map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))} placeholder="e.g. Mother, Foster carer" /></Field>
                    <Field label="Telephone"><Input data-testid={`parent-phone-${i}`} value={p.phone} onChange={(e) => setParents(parents.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} /></Field>
                    <Field label="Email"><Input data-testid={`parent-email-${i}`} type="email" value={p.email} onChange={(e) => setParents(parents.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} /></Field>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {[
                      ["is_primary_contact", "Primary contact"],
                      ["is_emergency_contact", "Emergency contact"],
                      ["authorised_to_receive_info", "Authorised to receive information"],
                    ].map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 min-h-[44px]">
                        <input type="checkbox" data-testid={`parent-${key}-${i}`} checked={p[key]}
                          onChange={(e) => setParents(parents.map((x, j) => j === i ? { ...x, [key]: e.target.checked } : x))}
                          className="h-4 w-4 accent-[#005EB8]" />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" className="h-11" data-testid="wizard-parent-add"
                onClick={() => setParents([...parents, { ...EMPTY_PARENT }])}>
                <Plus className="h-4 w-4 mr-2" /> Add another parent/carer
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-school">
              <Field label="School *" hint="The school record is linked, not duplicated. Add new schools from the Schools section.">
                <Select value={form.school_id} onValueChange={(v) => set("school_id", v)}>
                  <SelectTrigger data-testid="wizard-school-select"><SelectValue placeholder="Select school" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {schools.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`wizard-school-${s.school_number}`}>{s.name}{s.postcode ? ` — ${s.postcode}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {school && (
                <div className="rounded-md bg-slate-50 border border-border p-4 text-sm space-y-1" data-testid="wizard-school-summary">
                  <p className="font-medium text-slate-900">{school.name}</p>
                  {school.address && <p className="text-slate-600">{school.address}{school.postcode ? `, ${school.postcode}` : ""}</p>}
                  {school.primary_contact && <p className="text-slate-600">Contact: {school.primary_contact}</p>}
                  {school.senco_contact_name && <p className="text-slate-600">SENCO: {school.senco_contact_name}{school.senco_contact_email ? ` (${school.senco_contact_email})` : ""}</p>}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="wizard-step-send">
              <p className="text-xs text-slate-500 rounded-md bg-slate-50 border border-border p-3">
                Data minimisation: record only information relevant to delivering safe independent travel training.
                This is not a diagnostic medical record.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Primary SEND / support need"><Input data-testid="wizard-send-primary" value={form.send_primary_need} onChange={(e) => set("send_primary_need", e.target.value)} /></Field>
                <Field label="Additional support needs"><Input data-testid="wizard-send-additional" value={form.send_additional_needs} onChange={(e) => set("send_additional_needs", e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Communication needs"><Textarea data-testid="wizard-communication" rows={2} value={form.communication_needs} onChange={(e) => set("communication_needs", e.target.value)} /></Field>
                <Field label="Mobility requirements"><Textarea data-testid="wizard-mobility" rows={2} value={form.mobility_requirements} onChange={(e) => set("mobility_requirements", e.target.value)} /></Field>
                <Field label="Sensory considerations"><Textarea data-testid="wizard-sensory" rows={2} value={form.sensory_considerations} onChange={(e) => set("sensory_considerations", e.target.value)} /></Field>
                <Field label="Behavioural considerations relevant to safe travel"><Textarea data-testid="wizard-behavioural" rows={2} value={form.behavioural_considerations} onChange={(e) => set("behavioural_considerations", e.target.value)} /></Field>
                <Field label="Learning / understanding considerations"><Textarea data-testid="wizard-learning" rows={2} value={form.learning_considerations} onChange={(e) => set("learning_considerations", e.target.value)} /></Field>
                <Field label="Known triggers relevant to travel"><Textarea data-testid="wizard-triggers" rows={2} value={form.known_triggers} onChange={(e) => set("known_triggers", e.target.value)} /></Field>
                <Field label="Strategies that help the child"><Textarea data-testid="wizard-strategies" rows={2} value={form.strategies_help} onChange={(e) => set("strategies_help", e.target.value)} /></Field>
                <Field label="Required reasonable adjustments"><Textarea data-testid="wizard-adjustments" rows={2} value={form.reasonable_adjustments} onChange={(e) => set("reasonable_adjustments", e.target.value)} /></Field>
              </div>
              <Field label="Additional information relevant to safe travel">
                <Textarea data-testid="wizard-additional-info" rows={3} value={form.additional_info_safe_travel} onChange={(e) => set("additional_info_safe_travel", e.target.value)} />
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="wizard-step-health">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 flex gap-2" data-testid="health-warning">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 font-medium">
                  Only record health information necessary for safe delivery of the travel-training service.
                  Do not record general medical history.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Allergies relevant to training"><Textarea data-testid="wizard-allergies" rows={2} value={form.health_allergies} onChange={(e) => set("health_allergies", e.target.value)} /></Field>
                <Field label="Medication considerations for emergency response"><Textarea data-testid="wizard-medication" rows={2} value={form.health_medication} onChange={(e) => set("health_medication", e.target.value)} /></Field>
                <Field label="Medical conditions relevant to travel safety"><Textarea data-testid="wizard-conditions" rows={2} value={form.health_conditions} onChange={(e) => set("health_conditions", e.target.value)} /></Field>
                <Field label="Emergency action information"><Textarea data-testid="wizard-emergency-action" rows={2} value={form.health_emergency_action} onChange={(e) => set("health_emergency_action", e.target.value)} /></Field>
                <Field label="Mobility (health-related)"><Textarea data-testid="wizard-health-mobility" rows={2} value={form.health_mobility} onChange={(e) => set("health_mobility", e.target.value)} /></Field>
                <Field label="Other essential safety information"><Textarea data-testid="wizard-health-other" rows={2} value={form.health_other} onChange={(e) => set("health_other", e.target.value)} /></Field>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4" data-testid="wizard-step-consent">
              <p className="text-xs text-slate-500 rounded-md bg-slate-50 border border-border p-3">
                Record the operational status of each consent/authority. Lawful-basis decisions remain with
                authorised organisational staff. Do not assume parental consent is the sole basis for processing.
              </p>
              <Field label="Consent/authority provided by (name &amp; role)">
                <Input data-testid="wizard-consent-by" value={form.consent_provided_by} onChange={(e) => set("consent_provided_by", e.target.value)} placeholder="e.g. Ngozi Okafor — Mother" />
              </Field>
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-800">Parent/carer information letter — read before signing</p>
                <p className="text-xs text-slate-500">Show this to the parent/carer and ask them to read it in full before they sign below.</p>
                <ConsentLetter childName={[form.first_name || form.child_first_name || "", form.surname || form.last_name || ""].filter(Boolean).join(" ")} />
              </div>
              <div className="rounded-md border border-border p-3 space-y-2" data-testid="wizard-signature-block">
                <p className="text-sm font-medium text-slate-800">Parent/carer on-screen signature (optional)</p>
                <p className="text-xs text-slate-500">
                  If the parent/carer is present, they can sign below to confirm the consents recorded in this referral.
                  The signature is stored as a private, access-controlled document and linked to each consent record.
                </p>
                <SignaturePad value={form.signature_data} onChange={(d) => set("signature_data", d)} />
                {form.signature_data && (
                  <p className="text-xs font-medium text-emerald-700" data-testid="wizard-signature-captured">
                    Signature captured{form.consent_provided_by ? ` — will be recorded for ${form.consent_provided_by}` : ""}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                {CONSENT_TYPES.map((c) => (
                  <div key={c.key} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-md border border-border p-3" data-testid={`wizard-consent-${c.key}`}>
                    <span className="text-sm text-slate-800 flex-1">{c.label}</span>
                    <Select value={form.consents[c.key] || "none"} onValueChange={(v) => set("consents", { ...form.consents, [c.key]: v })}>
                      <SelectTrigger className="w-full sm:w-52" data-testid={`wizard-consent-select-${c.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="none">Not recorded</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="obtained">Obtained</SelectItem>
                        <SelectItem value="not_required">Not required</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-4" data-testid="wizard-step-documents">
              <p className="text-xs text-slate-500 rounded-md bg-slate-50 border border-border p-3">
                Files are stored privately with access controls — never publicly accessible. Max 10 MB. Accepted: JPEG, PNG, WebP, GIF, PDF.
              </p>
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 rounded-md border border-border p-3 text-sm" data-testid={`wizard-file-${i}`}>
                  <span className="flex-1 truncate">{f.file.name}</span>
                  <span className="text-xs text-slate-500">{f.category.replace(/_/g, " ")}</span>
                  <Button type="button" variant="ghost" className="h-9 text-red-600" data-testid={`wizard-file-remove-${i}`}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <FileAdder onAdd={(f) => setFiles([...files, f])} />
            </div>
          )}

          {step === 8 && (
            <div className="space-y-4" data-testid="wizard-step-assignment">
              <Field label="Assign Primary Travel Trainer (optional)" hint="Trainers only gain case access after a valid assignment.">
                <Select value={form.trainer_id || "none"} onValueChange={(v) => set("trainer_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="wizard-trainer-select"><SelectValue placeholder="Assign later" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Assign later</SelectItem>
                    {trainers.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`wizard-trainer-${t.staff_number}`}>{t.name} ({t.staff_number})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Assignment notes">
                <Textarea data-testid="wizard-assignment-notes" rows={2} value={form.assignment_notes} onChange={(e) => set("assignment_notes", e.target.value)} />
              </Field>
            </div>
          )}

          {step === 9 && (
            <div className="space-y-4" data-testid="wizard-step-review">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Review before submitting</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm rounded-md border border-border p-4">
                <div><dt className="text-slate-500">Child</dt><dd className="font-medium" data-testid="review-child-name">{form.first_name} {form.surname}{form.preferred_name ? ` (“${form.preferred_name}”)` : ""}</dd></div>
                <div><dt className="text-slate-500">Date of birth</dt><dd className="font-medium">{form.date_of_birth || "—"}</dd></div>
                <div><dt className="text-slate-500">Local authority</dt><dd className="font-medium" data-testid="review-council">{council?.name || "—"}</dd></div>
                <div><dt className="text-slate-500">School</dt><dd className="font-medium" data-testid="review-school">{school?.name || "—"}</dd></div>
                <div><dt className="text-slate-500">Council reference</dt><dd className="font-medium">{form.council_reference || "—"}</dd></div>
                <div><dt className="text-slate-500">Priority</dt><dd className="font-medium capitalize">{form.priority}</dd></div>
                <div><dt className="text-slate-500">Parents/carers</dt><dd className="font-medium">{parents.filter((p) => p.name.trim()).length}</dd></div>
                <div><dt className="text-slate-500">Consents recorded</dt><dd className="font-medium">{Object.values(form.consents).filter((v) => v && v !== "none").length}</dd></div>
                <div><dt className="text-slate-500">Documents</dt><dd className="font-medium">{files.length}</dd></div>
                <div><dt className="text-slate-500">Trainer</dt><dd className="font-medium">{trainer?.name || "Assign later"}</dd></div>
              </dl>
              <p className="text-xs text-slate-500">
                Submitting creates the child case (with a permanent Case ID), the referral record, parent/carer
                contacts, consent records, document uploads and any trainer assignment — each action is audit-logged.
              </p>
            </div>
          )}

          {duplicates && duplicates.length > 0 && step >= 1 && step < 9 && (
            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="duplicate-warning">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Possible existing child record found — please review before creating another record.
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-900">
                {duplicates.map((d) => (
                  <li key={d.id} data-testid={`duplicate-match-${d.case_id}`}>
                    {d.case_id} — {d.first_name} {d.surname} (DOB {d.date_of_birth || "unknown"}){d.school_name ? ` — ${d.school_name}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={back} disabled={step === 0} data-testid="wizard-back-button" className="h-11">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="flex gap-3">
          {step >= 1 && (
            <Button variant="outline" onClick={() => submit(true)} disabled={submitting} data-testid="wizard-draft-button" className="h-11">
              <Save className="h-4 w-4 mr-2" /> Save as draft
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button onClick={next} data-testid="wizard-next-button" className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={submitting} data-testid="wizard-submit-button"
              className="h-11 bg-ukgreen hover:bg-green-800 text-white font-semibold">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" /> Submit referral</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FileAdder({ onAdd }) {
  const [category, setCategory] = useState("referral_document");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  return (
    <div className="rounded-md border border-dashed border-slate-300 p-4 space-y-3" data-testid="wizard-file-adder">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="wizard-file-category"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="referral_document">Referral document</SelectItem>
            <SelectItem value="child_document">Case document</SelectItem>
            <SelectItem value="consent_document">Consent document</SelectItem>
            <SelectItem value="child_photo">Child photograph</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="wizard-file-description" />
        <Input type="file" accept="image/*,application/pdf" data-testid="wizard-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <Button type="button" variant="outline" className="h-10" data-testid="wizard-file-add-button"
        disabled={!file}
        onClick={() => {
          onAdd({ file, category, description });
          setFile(null);
          setDescription("");
        }}>
        <Plus className="h-4 w-4 mr-2" /> Add file
      </Button>
    </div>
  );
}
